import { requestRef, type MyRequest, type PersonSummary } from "@scheduler/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { NewRequest } from "./NewRequest";
import { RequestList } from "./RequestList";

/**
 * Submit, see status, cancel, and CHANGE (punch list S35, Jeremy's option B, 3 Nov 2026): Change
 * on a listed request opens the same form filled in with it, and the save replaces it through
 * the reschedule rule. The old request stays until the new one is approved; Back returns to a
 * new request. The form is mounted afresh per request (`key`), so it reads its start once.
 */
export function MyRequests({ me }: { me: PersonSummary }) {
  const api = useSchedulerApi();
  const [requests, setRequests] = useState<MyRequest[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Bumped after any change, so the new-request panel re-checks against current bookings.
  const [changes, setChanges] = useState(0);
  // The request the form is changing, or null for a new request.
  const [changing, setChanging] = useState<MyRequest | null>(null);

  const refresh = useCallback(async () => {
    try {
      setRequests(await api.myRequests());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your requests.");
    }
  }, [api]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => { void refresh(); }, [refresh, back]);

  // A request that can no longer be changed (cancelled, decided elsewhere) drops out of change mode.
  useEffect(() => {
    if (changing && requests && !requests.some((r) => r.id === changing.id && r.canChange)) setChanging(null);
  }, [changing, requests]);

  async function cancel(r: MyRequest) {
    if (!window.confirm(`Cancel ${requestRef(r.label, r.start, r.end)}? It stays on the record as cancelled.`)) return;
    setBusyId(r.id);
    try {
      await api.cancel(r.id);
      setMessage(`${requestRef(r.label, r.start, r.end)} cancelled.`);
      setChanges((n) => n + 1);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not cancel.");
    } finally {
      setBusyId(null);
    }
  }

  function change(r: MyRequest) {
    setMessage(null);
    setChanging(changing?.id === r.id ? null : r);
    window.scrollTo({ top: 0 });
  }

  return (
    <section aria-labelledby="my-requests">
      <div className="panel-head">
        <h2 id="my-requests" className="panel-title">
          {changing ? `Change ${requestRef(changing.label, changing.start, changing.end)}` : "New request"}
        </h2>
        {changing ? <button type="button" className="ghost" onClick={() => setChanging(null)}>Back</button> : null}
      </div>
      {changing ? <p className="hint changing-note">The old request stays until the new one is approved.</p> : null}
      <NewRequest
        key={changing?.id ?? "new"}
        me={me}
        refreshKey={changes}
        replacing={changing ?? undefined}
        onSubmitted={(m) => { setMessage(m); setChanging(null); setChanges((n) => n + 1); void refresh(); }}
      />

      <h2 id="my-requests-list" className="panel-title spaced">My requests</h2>
      {message ? <p className="toast" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
      {requests ? (
        <RequestList requests={requests} meId={me.id} onCancel={(r) => void cancel(r)} onChange={change} changingId={changing?.id ?? null} busyId={busyId} />
      ) : <p className="muted">Loading…</p>}
    </section>
  );
}
